import { subDays } from "date-fns";
import type { CheckGridOverviewItem, CheckGridRecurrence, CheckGridSubjectType } from "../types";
import { subjectTypeFromOverview } from "./hostEntity";

export type CreatedWithin = "any" | "7d" | "30d" | "90d";

export type CheckgridFilters = {
  query: string;
  /** Assigned-to filter — HQ `assigned_to` (at most one board-user id). */
  assignees: number[];
  /** Host entity kind — HQ `entity_type` (at most one). */
  entityTypes: CheckGridSubjectType[];
  /** `entityKey` values. */
  entities: string[];
  recurrence: CheckGridRecurrence[];
  /** HQ `created_within_days` when not `any`. */
  created: CreatedWithin;
};

export const CREATED_OPTIONS: { value: Exclude<CreatedWithin, "any">; label: string; days: 7 | 30 | 90 }[] = [
  { value: "7d", label: "7 days", days: 7 },
  { value: "30d", label: "30 days", days: 30 },
  { value: "90d", label: "90 days", days: 90 }
];

/** Map UI filter state → HQ board-list query params (rail list; Right now matches the same filters on its full list in `filterGrids`, including the search text). */
export function boardListQueryFilters(filters: CheckgridFilters): {
  assignedTo: number | null;
  filterEntityType: CheckGridSubjectType | null;
  createdWithinDays: 7 | 30 | 90 | null;
  search: string | null;
  recurrence: string | null;
} {
  const assignedTo = filters.assignees.length === 1 ? filters.assignees[0] : null;
  const filterEntityType = filters.entityTypes.length === 1 ? filters.entityTypes[0] : null;
  const createdWithinDays = CREATED_OPTIONS.find((option) => option.value === filters.created)?.days ?? null;
  return { assignedTo, filterEntityType, createdWithinDays, search: filters.query.trim() || null, recurrence: filters.recurrence.join(",") || null };
}

export const EMPTY_FILTERS: CheckgridFilters = {
  query: "",
  assignees: [],
  entityTypes: [],
  entities: [],
  recurrence: [],
  created: "any"
};

export function entityKey(grid: CheckGridOverviewItem): string | null {
  return grid.entity_name ? `${subjectTypeFromOverview(grid)}:${grid.entity_name}` : null;
}

/** Number of filter groups in use (the search box counts as one). */
export function countActiveFilters(filters: CheckgridFilters): number {
  return [filters.query.trim(), filters.assignees.length, filters.entityTypes.length, filters.entities.length, filters.recurrence.length, filters.created !== "any"].filter(Boolean)
    .length;
}

export function filterGrids(grids: CheckGridOverviewItem[], filters: CheckgridFilters, userName: (id: number) => string | undefined): CheckGridOverviewItem[] {
  const terms = filters.query.toLowerCase().split(/\s+/).filter(Boolean);
  const createdDays = CREATED_OPTIONS.find((option) => option.value === filters.created)?.days;
  const createdAfter = createdDays == null ? null : subDays(new Date(), createdDays).getTime();

  return grids.filter((grid) => {
    const assignees = grid.assigned_boarduser_ids ?? [];
    if (terms.length) {
      const haystack = [grid.name, grid.entity_name, ...assignees.map(userName)].filter(Boolean).join(" ").toLowerCase();
      if (!terms.every((term) => haystack.includes(term))) return false;
    }
    // The rail also sends these to the list API; matching here is what filters the Right now overview.
    if (filters.assignees.length && !assignees.some((id) => filters.assignees.includes(id))) return false;
    if (filters.entityTypes.length && !filters.entityTypes.includes(subjectTypeFromOverview(grid))) return false;
    if (filters.entities.length) {
      const key = entityKey(grid);
      if (!key || !filters.entities.includes(key)) return false;
    }
    if (filters.recurrence.length && !filters.recurrence.includes(grid.recurrence)) return false;
    if (createdAfter != null) {
      const created = grid.created_at ? new Date(grid.created_at).getTime() : NaN;
      if (!(created >= createdAfter)) return false;
    }
    return true;
  });
}

const RECURRENCE_VALUES: CheckGridRecurrence[] = ["none", "daily", "weekly", "monthly"];
const SUBJECT_TYPES: CheckGridSubjectType[] = ["task", "client", "resource"];

/** Filters worth remembering: everything except the search text. */
export function hasStoredFilters(filters: CheckgridFilters): boolean {
  return countActiveFilters({ ...filters, query: "" }) > 0;
}

/** Rebuild filters from persisted JSON, dropping anything malformed. */
export function sanitizeStoredFilters(raw: unknown): CheckgridFilters {
  const value = (raw && typeof raw === "object" ? raw : {}) as Partial<Record<keyof CheckgridFilters, unknown>>;
  const list = <T>(input: unknown, isValid: (item: unknown) => item is T): T[] => (Array.isArray(input) ? input.filter(isValid) : []);
  return {
    query: "",
    assignees: list(value.assignees, (item): item is number => typeof item === "number"),
    entityTypes: list(value.entityTypes, (item): item is CheckGridSubjectType => SUBJECT_TYPES.includes(item as CheckGridSubjectType)),
    entities: list(value.entities, (item): item is string => typeof item === "string"),
    recurrence: list(value.recurrence, (item): item is CheckGridRecurrence => RECURRENCE_VALUES.includes(item as CheckGridRecurrence)),
    created: CREATED_OPTIONS.some((option) => option.value === value.created) ? (value.created as CreatedWithin) : "any"
  };
}
